// Iter 47 — High-converting commercial showroom landing.
// Premium neon-dark layout with three new conversion engines:
//   A. Aggressive pain-hook hero + animated chat-mock with live SOS flash
//   B. Interactive ROI calculator (1-10 kiosk slider, €80/kiosk savings)
//   C. Premium gold-bordered pricing shield (60-day free + €8/mo forever)
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SalesChatWidget from "@/components/SalesChatWidget";
import {
  Camera,
  ShieldCheck,
  ScanLine,
  MessageCircle,
  Layers,
  Zap,
  Check,
  ArrowRight,
  Globe2,
  Siren,
  TrendingUp,
  Calculator,
  Sparkles,
} from "lucide-react";

const TRIAL_DAYS = 60;
const PRICE_EUR = 8;
const SAVINGS_PER_KIOSK = 80; // monthly Z-Report / cash-hole leakage saved per kiosk

// Iter 83 — 6 glassmorphic blocks aligned 1:1 with the production
// ecosystem (Iter 76 broadcast · Iter 77 EDI · Iter 78 IMAP · Iter 79
// JCC/Viva · Iter 80a/80b Daily Folders + categories · Iter 81 KRONOS
// · Iter 82 chained 1-2-3 close pipeline).
const FEATURES = [
  {
    icon: Zap,
    title_en: "Strict 1-2-3 End-Shift Pipeline",
    title_gr: "Στρατιωτικό Κλείσιμο Βάρδιας",
    body:
      "Ένα πράσινο κουμπί που ΠΡΩΤΑ ανοίγει το WhatsApp pre-filled με όλο το report, ΜΕΤΑ αρχειοθετεί τα πάντα στον σωστό φάκελο του περιπτέρου και στο ΤΕΛΟΣ κάνει ολικό Reset για τον επόμενο υπάλληλο.",
    accent: "emerald",
    emoji: "📱",
  },
  {
    icon: MessageCircle,
    title_en: "All-in-One WhatsApp Report",
    title_gr: "Υπερ-Πλήρης Αναφορά WhatsApp",
    body:
      "Λογιστική σύνοψη (Μετρητά, Visa, Wolt, Bolt, Foody, Λαχεία και το νέο ΚΡΟΝΟΣ / KRONOS) μαζί με φωτογραφίες Z-Report & VISA Slip, 100% ΔΩΡΕΑΝ μέσω native deep-link.",
    accent: "amber",
    emoji: "📊",
  },
  {
    icon: ScanLine,
    title_en: "Automated EDI & IMAP Ingestion",
    title_gr: "Αυτόματα Τιμολόγια Προμηθευτών",
    body:
      "Η Python διαβάζει XML/PDF τιμολόγια (Laiko, CTC, Βατικιώτης) απευθείας από το email σου και πετάει live Pop-up έγκρισης για ενημέρωση αποθήκης και πιστώσεων.",
    accent: "cyan",
    emoji: "📥",
  },
  {
    icon: Sparkles,
    title_en: "AI Credit & Invoice Reconciliation",
    title_gr: "Έξυπνο Πάντρεμα Πιστώσεων",
    body:
      "Αυτόματο background «πάντρεμα» αποδείξεων με τιμολόγια πίστωσης, κλειδώνοντας την ημερομηνία της επιταγής στο Master Calendar χωρίς διπλοεγγραφές.",
    accent: "violet",
    emoji: "🧠",
  },
  {
    icon: Layers,
    title_en: "Infinite Dynamic Subfolders Archive",
    title_gr: "Έξυπνο Ψηφιακό Αρχείο",
    body:
      "Αυτόματη δημιουργία υποφακέλων ανά ημερομηνία και κατηγορία (Z-Reports, VISA, Invoices/Προμηθευτής, Bills, SalaryAdvance) με φωτάκια status 🔴 Pending / 🟢 Paid ακριβώς όπως το σχεδίασες στο χαρτί.",
    accent: "rose",
    emoji: "📁",
  },
  {
    icon: Siren,
    title_en: "Live Updates · JCC/Viva Anti-Fraud",
    title_gr: "Live Updates & JCC/Viva",
    body:
      "Ακαριαία Pop-up αναβαθμίσεων (YES/NO) με υποχρεωτικό κλείδωμα νόμων (ΦΠΑ 15%). Προαιρετική διασύνδεση JCC/Viva για αυτόματο Card Push και live έλεγχο τράπεζας.",
    accent: "sky",
    emoji: "🔄",
  },
];

const ACCENT_MAP = {
  amber: "from-amber-500 to-amber-700",
  cyan: "from-cyan-500 to-cyan-700",
  violet: "from-violet-500 to-violet-700",
  emerald: "from-emerald-500 to-emerald-700",
  rose: "from-rose-500 to-rose-700",
  sky: "from-sky-500 to-sky-700",
};

function FeatureCard({ icon: Icon, title_en, title_gr, body, accent, emoji }) {
  const gradient = ACCENT_MAP[accent] || ACCENT_MAP.amber;
  return (
    <div
      data-testid={`landing-feature-${title_en.replace(/\W+/g, "-").toLowerCase()}`}
      className="group relative bg-white/[0.04] backdrop-blur border border-white/10 rounded-2xl p-7 hover:border-emerald-300/40 hover:shadow-xl hover:shadow-emerald-500/10 transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-white shadow-lg shrink-0`}>
          <Icon className="h-6 w-6" />
        </div>
        {emoji && (
          <span className="text-3xl leading-none mt-1" aria-hidden>{emoji}</span>
        )}
      </div>
      <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white leading-tight">
        {title_gr}
      </h3>
      <p className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-bold mt-1">
        {title_en}
      </p>
      <p className="text-sm text-slate-300 leading-relaxed mt-3">{body}</p>
    </div>
  );
}

// ────────────────────────────────────────────────  SECTION A · animated chat mock
// Pre-baked sequence of 4 messages that scroll into the panel on a loop. The
// 4th frame is a crimson SOS that pulses to demonstrate the real Iter 46 alert.
const CHAT_LOOP = [
  { station: "Vizantiou",   employee: "Giorgos", body: "Καλημέρα — άνοιξα ταμείο €0.00 baseline", kind: "msg",  side: "left" },
  { station: "Stavrou",     employee: "Maria",   body: "Επιβεβαίωση Z-Report Cyta €145.30 OK ✅", kind: "msg",  side: "right" },
  { station: "Kallipoleos", employee: "Andreas", body: "🚚 Έρχεται ο οδηγός σε 5'",              kind: "msg",  side: "left" },
  { station: "Aglantzia",   employee: "—",       body: "⚠️ SOS · Aglantzia requires immediate attention!", kind: "sos", side: "left" },
];

function HeroChatMock() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % CHAT_LOOP.length), 2200);
    return () => clearInterval(t);
  }, []);
  // Show frames up to step inclusive (rolling reveal)
  const visible = CHAT_LOOP.slice(0, step + 1);
  const sosActive = visible.some((m) => m.kind === "sos");

  return (
    <div className="relative" data-testid="landing-chat-mock">
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-emerald-500/10 blur-3xl rounded-3xl" />
      <div className={`relative bg-slate-900 border-2 ${sosActive ? "border-red-500 shadow-red-500/40" : "border-white/10 shadow-amber-500/10"} rounded-3xl p-5 shadow-2xl transition-colors duration-500`}>
        {/* Window chrome */}
        <div className="flex items-center gap-2 mb-4">
          <div className="h-2.5 w-2.5 rounded-full bg-rose-500" />
          <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          <p className="ml-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1">
            <MessageCircle className="h-3 w-3" />
            Intra-Kiosk Chat · Live
          </p>
          <div className="ml-auto flex items-center gap-1 text-[10px] text-emerald-300">
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            4 ONLINE
          </div>
        </div>

        {/* Sidebar + stream layout */}
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          {/* Stations sidebar */}
          <div className="bg-slate-950/60 border border-white/5 rounded-xl p-3 space-y-2">
            <p className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Network</p>
            {["Vizantiou", "Stavrou", "Kallipoleos", "Aglantzia"].map((name) => {
              const isSos = sosActive && name === "Aglantzia";
              return (
                <div key={name} className="flex items-center gap-2">
                  <span className="relative inline-flex h-2 w-2">
                    {isSos ? (
                      <>
                        <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                      </>
                    ) : (
                      <>
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                      </>
                    )}
                  </span>
                  <span className={`text-xs font-bold ${isSos ? "text-red-300" : "text-slate-200"}`}>
                    {name}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Message stream */}
          <div className="space-y-2 min-h-[180px]">
            {visible.map((m, i) => {
              const isSos = m.kind === "sos";
              const self = m.side === "right";
              return (
                <div
                  key={i}
                  className={`flex ${self ? "justify-end" : "justify-start"} animate-[fadeIn_0.4s_ease-out]`}
                >
                  <div
                    className={`max-w-[90%] rounded-xl px-3 py-1.5 text-[11px] shadow ${
                      isSos
                        ? "bg-red-600 text-white border border-red-400 animate-pulse"
                        : self
                        ? "bg-blue-600 text-white"
                        : "bg-slate-700 text-white"
                    }`}
                  >
                    <div className={`text-[9px] uppercase tracking-wider mb-0.5 ${
                      isSos ? "text-red-100" : self ? "text-blue-100" : "text-slate-300"
                    }`}>
                      {m.station} · {m.employee}{isSos && " · 🚨 SOS"}
                    </div>
                    <div className="leading-snug">{m.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SOS flash overlay */}
        {sosActive && (
          <div
            className="absolute inset-0 rounded-3xl bg-red-600/15 pointer-events-none animate-pulse"
            data-testid="landing-chat-sos-flash"
          />
        )}
      </div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-3 text-center font-mono">
        Live preview · Iter 46 Intra-Kiosk Chat Engine
      </p>
    </div>
  );
}

// ────────────────────────────────────────────────  SECTION B · ROI Calculator
function RoiCalculator() {
  const [kiosks, setKiosks] = useState(4);
  const monthlySavings = useMemo(() => kiosks * SAVINGS_PER_KIOSK, [kiosks]);
  const annualSavings = monthlySavings * 12;
  const netGain = monthlySavings - PRICE_EUR;

  return (
    <section
      className="relative px-6 sm:px-10 py-20 max-w-5xl mx-auto"
      data-testid="landing-roi-section"
    >
      <div className="text-center mb-10">
        <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-full">
          <Calculator className="h-3 w-3" />
          ROI Calculator · Υπολογιστής Εξοικονόμησης
        </p>
        <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight mt-4">
          Πόσα <span className="text-emerald-400">χάνεις</span> κάθε μήνα;
        </h2>
        <p className="text-slate-400 mt-3 max-w-xl mx-auto">
          Drag the slider to your store count. We compute the average monthly cash
          leakage you stop losing — based on real Cyprus kiosk audits.
        </p>
      </div>

      <div className="bg-gradient-to-br from-emerald-950/60 via-slate-950 to-slate-950 border-2 border-emerald-500/30 rounded-3xl p-8 sm:p-10 shadow-2xl shadow-emerald-500/10">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-center">
          {/* Slider */}
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-emerald-300">
              Number of Kiosks Owned · Αριθμός Περιπτέρων
            </label>
            <div
              className="mt-3 text-7xl font-black text-white tracking-tighter tabular-nums"
              data-testid="roi-kiosk-count"
            >
              {kiosks}
              <span className="text-2xl text-slate-500 font-bold ml-2">
                {kiosks === 1 ? "store" : "stores"}
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={kiosks}
              onChange={(e) => setKiosks(Number(e.target.value))}
              className="w-full mt-5 accent-emerald-500"
              data-testid="roi-kiosk-slider"
              aria-label="Number of kiosks"
            />
            <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-1">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <span key={n} className={n === kiosks ? "text-emerald-300 font-bold" : ""}>
                  {n}
                </span>
              ))}
            </div>
          </div>

          {/* Computed values */}
          <div className="space-y-4">
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-5">
              <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                Monthly revenue you stop losing
              </p>
              <p className="text-sm text-slate-400 mt-1 leading-snug">
                Missing Z-Reports + register holes · per Cyprus audit avg
              </p>
              <p
                className="mt-3 text-5xl sm:text-6xl font-black bg-clip-text text-transparent bg-gradient-to-br from-emerald-300 to-emerald-500 tabular-nums"
                data-testid="roi-monthly-savings"
              >
                €{monthlySavings.toLocaleString()}
              </p>
              <p className="text-[11px] text-slate-500 mt-1 font-mono">
                €{annualSavings.toLocaleString()} / year saved
              </p>
            </div>

            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
                <Zap className="h-3 w-3" />
                Your KioskForce ERP Subscription
              </p>
              <p className="mt-2 text-2xl font-black text-white">
                Only <span className="text-amber-400">€{PRICE_EUR}</span>/month
                <span className="text-sm text-slate-400 font-bold ml-2">· Flat Rate · Unlimited kiosks</span>
              </p>
              <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                  Net monthly gain
                </span>
                <span
                  className="text-3xl font-black text-emerald-400 tabular-nums"
                  data-testid="roi-net-gain"
                >
                  +€{netGain.toLocaleString()}
                </span>
              </div>
            </div>

            <Link
              to="/register"
              data-testid="roi-cta-trial"
              className="inline-flex items-center justify-center gap-2 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black uppercase tracking-widest px-6 py-4 rounded-xl shadow-lg shadow-emerald-500/30 transition-colors"
            >
              Capture this gain · Start free trial
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────  SECTION C · Premium Pricing Shield
function PricingShield() {
  return (
    <div
      data-testid="landing-pricing-card"
      className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-black border-[3px] border-amber-500/60 rounded-3xl p-8 sm:p-10 shadow-2xl shadow-amber-500/20 overflow-hidden"
    >
      {/* Gold corner glow */}
      <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-amber-500/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />

      <div className="relative">
        {/* 🔥 LAUNCH OFFER ribbon */}
        <div
          className="inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.18em] font-black text-slate-950 bg-gradient-to-r from-amber-300 via-amber-400 to-yellow-300 px-4 py-2 rounded-full shadow-lg shadow-amber-500/30 animate-pulse"
          data-testid="landing-launch-offer-banner"
        >
          <Sparkles className="h-4 w-4" />
          🔥 ΠΡΟΣΦΟΡΑ ΛΑΝΣΑΡΙΣΜΑΤΟΣ
        </div>

        <h3
          className="mt-5 text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight"
          data-testid="landing-shield-headline"
        >
          60 ΗΜΕΡΕΣ ΕΝΤΕΛΩΣ ΔΩΡΕΑΝ
          <span className="block text-emerald-400 text-lg sm:text-xl font-bold mt-1 uppercase tracking-widest">
            (Zero Risk · No Card Required)
          </span>
        </h3>
        <p className="mt-3 text-slate-300 text-base sm:text-lg">
          Έπειτα <span className="text-amber-400 font-black">μόνο €{PRICE_EUR}/μήνα</span> για πάντα ·
          <span className="text-emerald-300 font-bold"> PayPal Automatic Protection</span> · Cancel one tap.
        </p>

        {/* Price block */}
        <div className="mt-6 grid sm:grid-cols-2 gap-6 items-end">
          <div>
            <p className="text-amber-300 text-sm font-bold tracking-wide">
              2-Month Free Trial · Δωρεάν Δοκιμή
            </p>
            <h2
              className="text-5xl sm:text-6xl font-black text-white tracking-tight mt-2"
              data-testid="landing-trial-badge"
            >
              {TRIAL_DAYS} ημέρες
            </h2>
            <p className="text-[11px] uppercase tracking-widest text-slate-400 mt-2">
              Full feature access · no card on file
            </p>
          </div>

          <div className="sm:text-right">
            <p className="text-slate-400 text-sm">Μετά τη δοκιμή · After trial</p>
            <div className="flex items-baseline gap-2 sm:justify-end mt-1">
              <span
                className="text-6xl sm:text-7xl font-black bg-clip-text text-transparent bg-gradient-to-br from-amber-300 to-amber-500"
                data-testid="landing-price-eur"
              >
                €{PRICE_EUR}
              </span>
              <span className="text-xl font-bold text-slate-300">/ μήνα</span>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">
              flat · unlimited kiosks · cancel anytime
            </p>
          </div>
        </div>

        {/* Feature checklist */}
        <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px] text-slate-200">
          {[
            "Unlimited kiosks · unlimited registers",
            "AI-OCR invoice + Z-Report scanning",
            "Live Wolt / Bolt / Foody webhooks",
            "Multi-language EN · GR · AR",
            "Twilio WhatsApp owner alerts",
            "Supplier-folder + USB dual backup",
            "Cheque calendar & credit auto-bypass",
            "Whitelabel logo + theme colour",
            "🆕 Intra-Kiosk SOS chat broadcasts",
            "🆕 Kronos Z-Audit reconciliation",
          ].map((feat) => (
            <li key={feat} className="flex items-start gap-2">
              <Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <span>{feat}</span>
            </li>
          ))}
        </ul>

        {/* PayPal Hosted Subscription Button */}
        <div className="mt-8 p-5 rounded-2xl bg-white text-slate-900">
          <p className="text-sm font-bold text-slate-700 mb-2 text-center">
            Activate after trial · Ενεργοποίηση μετά τη δοκιμή
          </p>
          <form
            action="https://www.paypal.com/cgi-bin/webscr"
            method="post"
            target="_top"
            data-testid="landing-paypal-form"
            className="flex flex-col items-center"
          >
            <input type="hidden" name="cmd" value="_s-xclick" />
            <input
              type="hidden"
              name="hosted_button_id"
              value="82PVJ439WEH9A"
              data-testid="landing-paypal-hbid"
            />
            <input
              type="image"
              src="https://www.paypalobjects.com/en_US/i/btn/btn_subscribeCC_LG.gif"
              border="0"
              name="submit"
              alt="PayPal — The safer, easier way to pay online!"
              data-testid="landing-paypal-submit"
            />
            <img
              alt=""
              border="0"
              src="https://www.paypalobjects.com/en_US/i/scr/pixel.gif"
              width="1"
              height="1"
            />
          </form>
          <p className="text-[10px] text-center text-slate-500 mt-2 font-mono">
            Billed via PayPal · merchant Giannisleo51@gmail.com · Auto-Protected
          </p>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center justify-center">
          <Link
            to="/register"
            data-testid="landing-cta-trial"
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black uppercase tracking-widest px-6 py-3 rounded-xl shadow-lg shadow-amber-500/30 transition-colors"
          >
            Ξεκίνα τη Δωρεάν Δοκιμή
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/saas-login"
            data-testid="landing-cta-login"
            className="text-sm text-slate-300 hover:text-white underline underline-offset-4 decoration-amber-400/50"
          >
            Έχω ήδη λογαριασμό · Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────  Page composition
export default function Landing() {
  return (
    <div
      data-testid="landing-page"
      className="min-h-screen bg-slate-950 text-white selection:bg-amber-400 selection:text-slate-950"
    >
      {/* Brand bar */}
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-400 text-slate-950 flex items-center justify-center font-black text-lg">
            K
          </div>
          <div>
            <p className="text-lg font-black tracking-tight leading-none">
              KioskForce <span className="text-amber-400">ERP</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-1">
              Cyprus · Operator Suite
            </p>
          </div>
        </div>
        <Link
          to="/saas-login"
          data-testid="landing-nav-signin"
          className="text-sm text-slate-300 hover:text-amber-300 font-bold"
        >
          Sign in →
        </Link>
      </header>

      {/* SECTION A · Hero + animated chat mock */}
      <section className="px-6 sm:px-10 pt-10 pb-20 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-3 py-1 rounded-full">
              <Zap className="h-3 w-3" />
              Built for Cyprus Kiosks · Φτιαγμένο για Περίπτερα
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.08] mt-6"
              data-testid="landing-hero-title"
            >
              <span className="block text-white">
                Σταμάτα να χαρίζεις τα κέρδη σου.
              </span>
              <span className="block mt-3 text-xl sm:text-2xl lg:text-3xl font-black text-emerald-300 tracking-tight leading-snug">
                Το <span className="bg-clip-text text-transparent bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600">KioskForce ERP</span> είναι ο μοναδικός <span className="text-white">ψηφιακός φρουρός στην Κύπρο</span> που <span className="bg-emerald-500/15 text-emerald-200 px-2 py-0.5 rounded">κλειδώνει το ταμείο σου</span> και εξαφανίζει τις <span className="text-white">ταμειακές τρύπες</span> πριν ο υπάλληλος πατήσει <span className="bg-red-600/20 text-red-300 px-2 py-0.5 rounded">End Shift</span>.
              </span>
            </h1>
            <p
              className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed"
              data-testid="landing-hero-sub"
            >
              KioskForce ERP cross-checks <b>every cent</b> the moment your operator
              hits END SHIFT — cash, Visa, Wolt, Bolt, Foody, cheques, credit notes,
              Paysafe / Cyta / Epic / Cablenet vouchers. Bilingual EN/GR alerts go
              straight to your WhatsApp.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                data-testid="landing-hero-cta-trial"
                className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black uppercase tracking-widest px-7 py-4 rounded-xl shadow-2xl shadow-amber-500/30 transition-colors"
              >
                Start 60-day Free Trial
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#roi"
                data-testid="landing-hero-cta-roi"
                className="inline-flex items-center gap-2 border border-emerald-500/40 hover:border-emerald-300 hover:bg-emerald-500/10 text-emerald-300 font-bold uppercase tracking-widest px-7 py-4 rounded-xl transition-colors"
              >
                <Calculator className="h-4 w-4" />
                Calculate my savings
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                ["€0.00", "drawer baseline"],
                ["7-day", "Kronos audit window"],
                ["4 stores", "live SOS chat net"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="text-2xl font-black text-white">{n}</p>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500">
                    {l}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Animated chat mock with SOS flash */}
          <HeroChatMock />
        </div>
      </section>

      {/* Iter 58 · 2×2 Glassmorphic Feature Showcase + Cyprus Trust badge */}
      <section
        className="px-6 sm:px-10 py-16 max-w-7xl mx-auto"
        data-testid="landing-showcase-section"
      >
        <div className="text-center mb-10">
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-3 py-1 rounded-full">
            <Sparkles className="h-3 w-3" />
            Core Premium Upgrades · Premium Δυνατότητες
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {[
            {
              k: "whatsapp",
              icon: "📱",
              title_gr: "Live WhatsApp Z-Reports",
              body:
                "Αυτόματη σύνοψη βάρδιας, φωτογραφίες ταμείου και φωνητικά σημειώματα κατευθείαν στο κινητό σου.",
              accent: "from-emerald-500/30 to-emerald-700/10",
              border: "border-emerald-400/40 hover:border-emerald-300",
              glow: "shadow-emerald-500/20",
            },
            {
              k: "chat-sos",
              icon: "🚨",
              title_gr: "Chat & Κόκκινος Συναγερμός",
              body:
                "Real-time επικοινωνία μεταξύ των καταστημάτων σου με σειρήνα SOS για απόλυτη ασφάλεια.",
              accent: "from-red-500/30 to-rose-700/10",
              border: "border-red-400/40 hover:border-red-300",
              glow: "shadow-red-500/20",
            },
            {
              k: "voip",
              icon: "📞",
              title_gr: "VoIP Intercom Calls",
              body:
                "Δωρεάν δικτυακές κλήσεις φωνής με 1 κλικ απευθείας από την οθόνη του τερματικού.",
              accent: "from-blue-500/30 to-indigo-700/10",
              border: "border-blue-400/40 hover:border-blue-300",
              glow: "shadow-blue-500/20",
            },
            {
              k: "payroll",
              icon: "🕒",
              title_gr: "Google iCal Payroll",
              body:
                "Αυτόματος υπολογισμός ωρών, πρόγραμμα βαρδιών και ακαριαία αφαίρεση των έναντι από τον μισθό.",
              accent: "from-amber-500/30 to-amber-700/10",
              border: "border-amber-400/40 hover:border-amber-300",
              glow: "shadow-amber-500/20",
            },
          ].map((c) => (
            <div
              key={c.k}
              data-testid={`landing-showcase-${c.k}`}
              className={`group relative overflow-hidden rounded-2xl border-2 ${c.border} bg-white/[0.04] backdrop-blur-xl shadow-lg ${c.glow} transition-all hover:-translate-y-0.5`}
            >
              {/* glow blob */}
              <div className={`absolute -top-16 -right-16 h-48 w-48 rounded-full bg-gradient-to-br ${c.accent} blur-3xl pointer-events-none group-hover:scale-110 transition-transform`} />
              <div className="relative p-6">
                <div className="text-4xl mb-2 transition-transform group-hover:scale-110">{c.icon}</div>
                <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-tight">
                  {c.title_gr}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed mt-3">{c.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Iter 83 · Cyprus Enterprise Assured trust shield — gold-bordered */}
        <div
          className="mt-10 relative overflow-hidden rounded-2xl border-[3px] border-amber-500/70 bg-gradient-to-br from-slate-900 via-slate-950 to-black p-6 sm:p-8 shadow-2xl shadow-amber-500/20"
          data-testid="landing-cyprus-trust-badge"
        >
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-5 flex-wrap">
            <div className="text-6xl shrink-0 drop-shadow-lg" aria-hidden>🇨🇾</div>
            <div className="flex-1 min-w-[260px]">
              <p
                className="text-[10px] uppercase tracking-[0.3em] font-black text-amber-300"
                data-testid="landing-trust-overline"
              >
                Cyprus Enterprise Assured
              </p>
              <p
                className="text-lg sm:text-xl font-black text-white tracking-tight leading-snug mt-1"
                data-testid="landing-trust-headline"
              >
                100% Συμβατό με τον νέο Εταιρικό Φόρο
                <span className="text-amber-400"> 15%</span>,
                <span className="text-amber-400"> TFA Form 4</span> και τις υπηρεσίες
                <span className="text-emerald-300"> Paysafe</span> ·
                <span className="text-emerald-300"> Cyta</span> ·
                <span className="text-emerald-300"> Epic</span> ·
                <span className="text-emerald-300"> MTN</span> ·
                <span className="text-emerald-300"> Aircash</span>.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {[
                  "✓ ΦΠΑ 15% LOCKED",
                  "✓ TFA Form 4",
                  "✓ Paysafe · Cyta · Epic · MTN · Aircash",
                  "✓ tenant_id firewall",
                ].map((badge) => (
                  <span
                    key={badge}
                    className="text-[10px] uppercase tracking-widest text-amber-300 bg-amber-500/15 border border-amber-500/40 px-3 py-1.5 rounded-full font-bold"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION B · ROI Calculator */}
      <div id="roi" className="bg-black/40 border-y border-white/5">
        <RoiCalculator />
      </div>

      {/* FEATURES — Iter 83 · 6-Grid Glassmorphic Interactive Showroom */}
      <section className="px-6 sm:px-10 py-20 border-y border-white/5" id="features">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-300">
            What&apos;s inside · Τι περιλαμβάνει
          </p>
          <h2
            className="text-4xl sm:text-5xl font-black text-white tracking-tight mt-3 max-w-3xl"
            data-testid="landing-features-title"
          >
            6 μηχανισμοί που δουλεύουν την ώρα που εσύ κοιμάσαι.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl text-base leading-relaxed">
            Όλη η αλυσίδα — από το σκανάρισμα του τιμολογίου μέχρι το pre-filled WhatsApp στο κινητό σου — δουλεύει χωρίς να αγγίξεις το πληκτρολόγιο.
          </p>

          <div
            className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="landing-features-grid"
          >
            {FEATURES.map((f) => (
              <FeatureCard key={f.title_en} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION C · Premium Pricing Shield */}
      <section id="pricing" className="px-6 sm:px-10 py-20 max-w-4xl mx-auto">
        <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-300 text-center flex items-center gap-2 justify-center">
          <Siren className="h-3 w-3" />
          Pricing Shield · Ασπίδα Τιμής
        </p>
        <h2
          className="text-4xl sm:text-5xl font-black text-white tracking-tight mt-3 text-center"
          data-testid="landing-pricing-title"
        >
          Δύο μήνες ΔΩΡΕΑΝ.
          <br />
          <span className="text-amber-400">Μετά μόνο €{PRICE_EUR}/μήνα.</span>
        </h2>
        <p className="text-slate-400 text-center mt-3 max-w-xl mx-auto">
          One plan. Every feature. Cancel from inside the Private Portal in one click —
          no email, no phone call.
        </p>

        <div className="mt-10">
          <PricingShield />
        </div>
      </section>

      {/* Footer — "Made by John" lives globally in /public/index.html */}
      <footer className="px-6 sm:px-10 py-10 border-t border-white/5 text-center">
        <p className="text-slate-500 text-xs">
          © {new Date().getFullYear()} KioskForce ERP · Cyprus · All rights reserved.
        </p>
      </footer>

      {/* Iter 55 — Public AI Sales Assistant floating widget */}
      <SalesChatWidget />

      {/* Light fadeIn keyframe used by the chat mock */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
